import { Injectable } from '@angular/core';
import { ReferenceFieldAPI } from '@core/typings/api/reference-fields.typing';
import { Automation } from '@core/typings/ui/automation.typing';
import { ReferenceFieldsUI } from '@core/typings/ui/reference-fields.typing';
import { CustomDataTablesService } from '@features/custom-data-tables/services/custom-data-table.service';
import { UserService } from '@features/users/user.service';
import { I18nService } from '@yourcause/common/i18n';
import { uniq } from 'lodash';
import { FormFieldHelperService } from './form-field-helper.service';

@Injectable({ providedIn: 'root' })
export class FormFieldAutomationService {

  constructor (
    private formFieldHelperService: FormFieldHelperService,
    private userService: UserService,
    private i18n: I18nService,
    private customDataTablesService: CustomDataTablesService
  ) { }

  async adaptFieldIdsForAutomation (
    referenceFieldIds: number[]
  ): Promise<Automation.ObjectConfig> {
    if (referenceFieldIds.length === 0) {
      return undefined;
    }
    const guids = uniq(referenceFieldIds.map((id) => {
      const field = this.formFieldHelperService.referenceFieldMapById[id];

      return field.customDataTableGuid;
    }).filter((guid) => !!guid));
    for (const guid of guids) {
      await this.customDataTablesService.setCustomDataTableOptionsFromGuid(
        guid,
        this.userService.getCurrentUserCulture()
      );
    }

    return {
      label: this.i18n.translate(
        'GLOBAL:textGrantManagerFields',
        {},
        'Grant manager fields'
      ),
      key: 'referenceFields',
      columns: referenceFieldIds.map((id) => {
        const field = this.formFieldHelperService.referenceFieldMapById[id];

        return this.refFieldTypeToAutomation(field.name, field);
      })
    };
  }

  refFieldTypeToAutomation (
    label: string,
    refField: ReferenceFieldAPI.ReferenceFieldDisplayModel
  ): Automation.ObjectColumnConfig {
    const baseConfig: Automation.CoreObjectColumnType = {
      label,
      key: refField.key
    };

    switch (refField.type) {
      case ReferenceFieldsUI.ReferenceFieldTypes.CustomDataTable:
      case ReferenceFieldsUI.ReferenceFieldTypes.Radio:
      case ReferenceFieldsUI.ReferenceFieldTypes.SelectBoxes:
      case ReferenceFieldsUI.ReferenceFieldTypes.PicklistGroup:
        const selectedCustomDataTable = refField.customDataTableGuid;
        const options = this.customDataTablesService.customDataTableOptionsMap[selectedCustomDataTable];

        return <Automation.ObjectColumnSelectType>{
          ...baseConfig,
          comparison: Automation.Comparisons.SelectIs,
          options: options ? options.map((item) => {
            return {
              label: item.value,
              value: item.key
            };
          }) : []
        };

      case ReferenceFieldsUI.ReferenceFieldTypes.Number:
      case ReferenceFieldsUI.ReferenceFieldTypes.Aggregate:
      case ReferenceFieldsUI.ReferenceFieldTypes.Currency:
        return {
          ...baseConfig,
          comparison: Automation.Comparisons.NumberRange
        };
      case ReferenceFieldsUI.ReferenceFieldTypes.Date:
        return {
          ...baseConfig,
          comparison: Automation.Comparisons.DateRange
        };
      default:
      case ReferenceFieldsUI.ReferenceFieldTypes.TextArea:
      case ReferenceFieldsUI.ReferenceFieldTypes.ExternalAPI:
      case ReferenceFieldsUI.ReferenceFieldTypes.TextField:
        return {
          ...baseConfig,
          comparison: Automation.Comparisons.TextIs
        };
      case ReferenceFieldsUI.ReferenceFieldTypes.Checkbox:
        return {
          ...baseConfig,
          comparison: Automation.Comparisons.IsSelected
        };
      case ReferenceFieldsUI.ReferenceFieldTypes.FileUpload:
        return {
          ...baseConfig,
          comparison: Automation.Comparisons.IsUploaded
        };
    }
  }

}
